import axios from 'axios';
import { authHeader } from '@/lib/events';
import type { SentEmail } from '@/lib/outreach-email';
import type { AccountDetail } from '@/lib/sales-data';

/**
 * Sales → Repeat clients: the work queue of past clients due a call. The
 * queue is built, filtered, sorted and paged on the server.
 */

export const REPEAT_CLIENTS_PATH = '/repeat-clients';
/** One rep's queue, so Back returns to the same rep. */
export const repeatClientsRepPath = (repId: string): string => `${REPEAT_CLIENTS_PATH}/rep/${encodeURIComponent(repId)}`;
/** The rep id that lists every rep's clients at once (view_all_repeat_clients). */
export const ALL_REPS = 'all';

export const QUEUE_STATUS = {
  toContact: 'to-contact',
  contacted: 'contacted',
  snoozed: 'snoozed',
  notInterested: 'not-interested',
  booked: 'booked',
} as const;
export type QueueStatus = (typeof QUEUE_STATUS)[keyof typeof QUEUE_STATUS];

/**
 * How a past deal ended: `booked`, `stage:<StageName>` (a cancellation, On
 * Hold, Unresponsive…) or `lost:<reason>` for Closed Lost. The queue filters on
 * these; Booked is the default.
 */
export const OUTCOME_BOOKED = 'booked';
/** Sent when every outcome is wanted (the dropdown cleared). */
export const OUTCOME_ANY = 'any';
export const DEFAULT_OUTCOMES: readonly string[] = [OUTCOME_BOOKED];

export interface OutcomeOption {
  readonly key: string;
  readonly label: string;
  /** How many of the rep's clients have a deal that ended this way. */
  readonly clients: number;
}

/** How an outcome key reads ("lost:Limited Budget" → "Lost: Limited Budget"). */
export function outcomeKeyLabel(key: string): string {
  if (key === OUTCOME_BOOKED) return 'Booked';
  if (key.startsWith('lost:')) return `Lost: ${key.slice('lost:'.length) || 'no reason given'}`;
  return key.startsWith('stage:') ? key.slice('stage:'.length) : key;
}

/** How one past deal ended, as its pill reads. */
export function dealOutcomeLabel(deal: Pick<QueueDeal, 'booked' | 'stageName' | 'reasonLost'>): string {
  if (deal.booked) return 'Booked';
  if (deal.stageName === 'Closed Lost') return `Lost: ${deal.reasonLost || 'no reason given'}`;
  return deal.stageName;
}

export const QUEUE_SORT = {
  suggested: 'suggested',
  account: 'account',
  nextEvent: 'nextEvent',
  bookings: 'bookings',
  leads: 'leads',
  value: 'value',
} as const;
export type QueueSort = (typeof QUEUE_SORT)[keyof typeof QUEUE_SORT];

export interface QueueDeal {
  readonly id: string;
  readonly name: string;
  readonly stageName: string;
  readonly programName: string;
  /** When they reached out (the deal's created date). */
  readonly reachedOutDate: string;
  readonly reachOutDate: string;
  readonly eventDate: string | null;
  readonly nextEventDate: string | null;
  /** Signed; otherwise a lead that did not close. */
  readonly booked: boolean;
  /** Reason_Lost__c on a lost deal; '' otherwise. */
  readonly reasonLost: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly totalCost: number | null;
}

export interface QueueRow {
  readonly accountId: string;
  readonly accountName: string;
  readonly status: QueueStatus;
  readonly snoozedUntil: string | null;
  readonly statusChangedAt: string | null;
  readonly suggestedReachOutDate: string;
  readonly nextEventDate: string | null;
  readonly bookings: number;
  readonly leads: number;
  readonly bookedValue: number;
  readonly programName: string;
  /** The rep on the latest matching deal: whose client it is. */
  readonly ownerId: string;
  readonly ownerName: string;
  readonly deals: readonly QueueDeal[];
  /** The newest email sent to the client from bcecloud; null when none. */
  readonly lastEmail: LastEmail | null;
}

/** An email sent from bcecloud, as the queue row shows it. */
export interface LastEmail {
  readonly sentAt: string;
  readonly to: string;
  readonly subject: string;
  readonly opens: number;
  readonly lastOpenedAt: string | null;
}

export interface RepOption {
  readonly ownerId: string;
  readonly ownerName: string;
}

export interface RepeatClientsPage {
  readonly rows: readonly QueueRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly counts: Readonly<Record<QueueStatus, number>>;
  /** Every outcome among the rep's clients, Booked first, then most clients. */
  readonly outcomeOptions: readonly OutcomeOption[];
  readonly today: string;
  readonly reachOutBy: string;
  readonly owner: RepOption | null;
  readonly viewingSelf: boolean;
  /** Every rep's clients at once. */
  readonly allReps: boolean;
  readonly reps: readonly RepOption[];
}

export interface QueueRequest {
  readonly ownerId?: string;
  readonly status: QueueStatus;
  /** Outcome keys; undefined is the default (Booked), empty is any outcome. */
  readonly outcomes?: readonly string[];
  readonly reachOutBy?: string;
  readonly q: string;
  readonly sort?: QueueSort;
  readonly dir?: 'asc' | 'desc';
  readonly page: number;
}

const BASE = '/api/v3/repeat-clients';

export async function readRepeatClients(request: QueueRequest, signal?: AbortSignal): Promise<RepeatClientsPage> {
  const params = new URLSearchParams({ status: request.status, q: request.q, page: String(request.page) });
  if (request.outcomes !== undefined) {
    if (request.outcomes.length === 0) params.append('outcome', OUTCOME_ANY);
    for (const outcome of request.outcomes) params.append('outcome', outcome);
  }
  if (request.ownerId) params.set('ownerId', request.ownerId);
  if (request.reachOutBy) params.set('reachOutBy', request.reachOutBy);
  if (request.sort) params.set('sort', request.sort);
  if (request.dir) params.set('dir', request.dir);
  const response = await axios.get<{ data: RepeatClientsPage }>(`${BASE}?${params.toString()}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function setRepeatClientStatus(
  accountId: string,
  body: { readonly status: QueueStatus; readonly snoozeUntil?: string; readonly ownerId?: string },
): Promise<void> {
  await axios.patch(`${BASE}/${encodeURIComponent(accountId)}/status`, body, { headers: await authHeader() });
}

/** One client's page: everything about them, from the queue's row. */
export const REPEAT_CLIENT_ROUTE = `${REPEAT_CLIENTS_PATH}/clients/:accountId`;
export const repeatClientPath = (accountId: string): string => `${REPEAT_CLIENTS_PATH}/clients/${encodeURIComponent(accountId)}`;

export const CLIENT_ACTIVITY_KIND = { email: 'email', call: 'call', todo: 'todo', meeting: 'meeting' } as const;
export type ClientActivityKind = (typeof CLIENT_ACTIVITY_KIND)[keyof typeof CLIENT_ACTIVITY_KIND];

/** One touch with the client, live from Salesforce. Subjects only. */
export interface ClientActivity {
  readonly id: string;
  readonly kind: ClientActivityKind;
  readonly title: string;
  readonly at: string;
  readonly contactName: string;
  readonly dealName: string;
  readonly ownerName: string;
}

/** What GET /repeat-clients/clients/:accountId returns. */
export interface RepeatClientDetail extends AccountDetail {
  readonly activity: readonly ClientActivity[];
  /** False when Salesforce couldn't be reached and only deal activity shows. */
  readonly activityLive: boolean;
  readonly sentEmails: readonly SentEmail[];
  readonly status: QueueStatus;
  readonly snoozedUntil: string | null;
}

export const repeatClientQueryKey = (accountId: string) => ['repeat-clients', 'client', accountId] as const;

export async function readRepeatClient(accountId: string, signal?: AbortSignal): Promise<RepeatClientDetail> {
  const response = await axios.get<{ data: RepeatClientDetail }>(`${BASE}/clients/${encodeURIComponent(accountId)}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}
